import { FormEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import AsyncSelect from "react-select/async";
import { RootDispatch, RootState } from "../../../store";
import {
  clearSelectedRequestItems,
  getFloorRequestById,
} from "../../../store/slices/Factory/newFloorSlice";
import { IFloorRequest, IFloorRequestItem } from "../../../models/floor.model";
import ConfirmationModal from "../../../components/Assets/ConfirmationModal";
import { replaceReturnRequest } from "../../../store/slices/Factory/materialReturnSlice";

interface FloorMaterialReturnRequestModalProps {
  batch: IFloorRequest;
  onClose: (success: boolean) => void;
}

interface FloorMaterialOption {
  value: string;
  label: string;
  item: IFloorRequestItem;
}

export default function FloorMaterialReturnRequestModal({
  batch,
  onClose,
}: FloorMaterialReturnRequestModalProps) {
  const dispatch: RootDispatch = useDispatch();
  const loading = useSelector(
    (state: RootState) => state.newFloor.loading.getFloorRequestByIdLoading,
  );
  const submitting = useSelector(
    (state: RootState) => state.newFloor.loading.replaceReturnRequestLoading,
  );
  const [selectedItem, setSelectedItem] = useState<IFloorRequestItem | null>(
    null,
  );
  const [quantity, setQuantity] = useState(0);
  const [reason, setReason] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const requestId = batch._id;
  const assignedQuantity = selectedItem?.fulfilledQuantity || 0;

  const loadMaterialOptions = async (
    materialSearch: string,
  ): Promise<FloorMaterialOption[]> => {
    try {
      const response = await dispatch(
        getFloorRequestById({ id: requestId, materialSearch }),
      ).unwrap();
      return (response.data || [])
        .filter(
          (item: IFloorRequestItem) =>
            item.rawMaterialId && item.fulfilledQuantity > 0,
        )
        .map((item: IFloorRequestItem) => ({
          value: item._id,
          label: `${item.rawMaterialId.name} · Assigned ${item.fulfilledQuantity} ${item.unit}`,
          item,
        }));
    } catch {
      return [];
    }
  };

  const close = (success: boolean) => {
    dispatch(clearSelectedRequestItems());
    onClose(success);
  };

  const requestConfirmation = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedItem || !selectedItem.rawMaterialId) {
      toast.error("Select a material from this batch.");
      return;
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      toast.error("Quantity must be greater than zero.");
      return;
    }
    if (quantity > assignedQuantity) {
      toast.error(
        `Quantity cannot exceed the assigned amount (${assignedQuantity}).`,
      );
      return;
    }
    if (!reason.trim()) {
      toast.error("Enter a reason for the return or replacement.");
      return;
    }
    setShowConfirmation(true);
  };

  const submit = async () => {
    if (
      !selectedItem?.rawMaterialId ||
      quantity <= 0 ||
      quantity > assignedQuantity
    ) {
      setShowConfirmation(false);
      return;
    }
    try {
      await dispatch(
        replaceReturnRequest({
          requestId,
          materialId: selectedItem.rawMaterialId._id,
          quantity,
          unit: selectedItem.unit,
          reason: reason.trim(),
        }),
      ).unwrap();
      close(true);
    } catch (error) {
      toast.error(
        typeof error === "string" ? error : "Could not submit the request.",
      );
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
        <section
          role="dialog"
          aria-modal="true"
          aria-labelledby="return-request-title"
          className="w-full max-w-xl overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
        >
          <header className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
            <div>
              <h2
                id="return-request-title"
                className="text-lg font-semibold text-gray-900"
              >
                Replace or Return Material
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Batch #{batch.batchNo ?? "N/A"}
              </p>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={() => close(false)}
              className="rounded p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            >
              ×
            </button>
          </header>

          <form onSubmit={requestConfirmation}>
            <div className="space-y-4 px-5 py-5">
              <div>
                <label
                  htmlFor="return-material"
                  className="mb-1.5 block text-sm font-medium text-gray-700"
                >
                  Material
                </label>
                <AsyncSelect<FloorMaterialOption, false>
                  inputId="return-material"
                  cacheOptions
                  defaultOptions
                  loadOptions={loadMaterialOptions}
                  value={
                    selectedItem
                      ? {
                          value: selectedItem._id,
                          label: `${selectedItem.rawMaterialId.name} · Assigned ${selectedItem.fulfilledQuantity} ${selectedItem.unit}`,
                          item: selectedItem,
                        }
                      : null
                  }
                  onChange={(option) => {
                    const item = option?.item || null;
                    setSelectedItem(item);
                    setQuantity(item?.fulfilledQuantity || 0);
                  }}
                  isClearable
                  isLoading={loading}
                  loadingMessage={() => "Loading assigned materials..."}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue
                      ? "No assigned materials match this search."
                      : "No assigned materials available for return."
                  }
                  placeholder="Search materials in this request..."
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "42px",
                      borderColor: "#D1D5DB",
                      boxShadow: "none",
                      "&:hover": { borderColor: "#9CA3AF" },
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>

              {selectedItem && (
                <div>
                  <label
                    htmlFor="return-quantity"
                    className="mb-1.5 block text-sm font-medium text-gray-700"
                  >
                    Quantity{" "}
                    <span className="font-normal text-gray-500">
                      (max {assignedQuantity} {selectedItem.unit})
                    </span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="return-quantity"
                      type="number"
                      min="0.01"
                      max={assignedQuantity}
                      step="any"
                      required
                      value={quantity || ""}
                      onChange={(event) =>
                        setQuantity(Number(event.target.value))
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                    <span className="min-w-16 text-sm text-gray-600">
                      {selectedItem.unit}
                    </span>
                  </div>
                </div>
              )}

              <div>
                <label
                  htmlFor="return-reason"
                  className="mb-1.5 block text-sm font-medium text-gray-700"
                >
                  Reason
                </label>
                <textarea
                  id="return-reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  rows={3}
                  required
                  maxLength={500}
                  className="w-full resize-y rounded-md border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  placeholder="Describe why this material is being returned or replaced"
                />
              </div>
            </div>

            <footer className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-4">
              <button
                type="button"
                onClick={() => close(false)}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !selectedItem}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Review Request
              </button>
            </footer>
          </form>
        </section>
      </div>

      <ConfirmationModal
        isOpen={showConfirmation}
        title="Confirm Material Request"
        subTitle={selectedItem?.rawMaterialId.name || "Selected material"}
        description={`Batch #${batch.batchNo ?? "N/A"} · ${quantity} ${selectedItem?.unit || ""}. Reason: ${reason.trim()}`}
        confirmText="Submit Request"
        cancelText="Back"
        danger={false}
        loading={submitting}
        onConfirm={submit}
        onClose={() => setShowConfirmation(false)}
      />
    </>
  );
}
